import { TabsWithContent, TabWithContent } from "@homebound/beam";
import { ContractType, Stage } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { useStripStage } from "src/hooks/useStripStage";
import { useTabParam } from "src/hooks/useTabParam";
import { PageHeader } from "src/routes/layout/PageHeader";
import { useProjectContext } from "src/routes/projects/context/ProjectContext";
import { ChangeOrderDetailsTab } from "src/routes/projects/homeowner-contracts/ChangeOrderDetailsTab";
import { DrawScheduleTab } from "src/routes/projects/homeowner-contracts/DrawScheduleTab";
import { useHomeownerContractChangeOrder2Query } from "src/routes/projects/homeowner-contracts/HomeownerContractChangeOrderPage.generated";
import { createHomeownerContractsUrl } from "src/RouteUrls";
import { queryResult } from "src/utils/queryResult";
import { InvoiceScheduleTab } from "./InvoiceScheduleTab";

type RouteParams = { projectId: string; changeOrderId: string };

const drawSchedulesSupportedInStages = [Stage.PreConExecution, Stage.Construction];

export function HomeownerContractChangeOrderPage() {
  useStripStage();
  const [tab, setTab] = useTabParam("details");
  const { projectId, changeOrderId } = useRouteParams<RouteParams>();
  const { clientContract } = useProjectContext();
  const query = useHomeownerContractChangeOrder2Query({ variables: { changeOrderId } });

  return queryResult(query, {
    data(data) {
      const tabs: TabWithContent[] = [];

      tabs.push({
        name: "Details",
        value: "details",
        render: () => <ChangeOrderDetailsTab changeOrderId={changeOrderId} projectId={projectId} />,
      });

      const { contractType, contract } = data.homeownerContractChangeOrder;
      if (
        contractType === ContractType.Fixed &&
        drawSchedulesSupportedInStages.includes(contract.projectStage.stage.code)
      ) {
        tabs.push({
          name: "Draw Schedule",
          value: "draws",
          render: () => (
            <DrawScheduleTab
              contractId={contract.id}
              changeOrderId={changeOrderId}
              stage={contract.projectStage.stage.code}
            />
          ),
        });
      }

      if (!!contract.projectStage.project.invoiceSchedule) {
        tabs.push({
          name: "Invoice Schedule",
          value: "invoice",
          render: () => <InvoiceScheduleTab invoiceSchedule={contract.projectStage.project.invoiceSchedule!} />,
        });
      }

      return (
        <>
          <PageHeader
            title={data.homeownerContractChangeOrder.name}
            breadcrumb={{ label: `All ${clientContract}s`, href: createHomeownerContractsUrl(projectId) }}
          />
          <TabsWithContent selected={tab} onChange={setTab} tabs={tabs} />
        </>
      );
    },
  });
}
